<template>
    <div class="recipient-select">
        <div class="header">
            <label>{{ $t("wp.sendMessageTo") }}</label>
            <a-radio-group :default-value="target" @change="onChange">
                <a-radio-button value="all" v-if="!value.channel"> {{ $t("wp.allContact") }}</a-radio-button>
                <a-radio-button value="customer-groups">{{ $t("wp.customerGroups") }}</a-radio-button>
                <!--                <a-radio-button value="custom-list"> Custom List</a-radio-button>-->
                <!--                <a-radio-button value="import"> Import From File</a-radio-button>-->
            </a-radio-group>
        </div>

        <div>
            <div v-if="target === 'all'">
                <a-table
                    :columns="cols"
                    :dataSource="contactList"
                    :pagination="pagination"
                    @change="handleTableChange"
                    :key="'vtable-' + renderCount"
                    :locale="{
                        emptyText: $t('message.noContactFound'),
                    }"
                    rowKey="id"
                    size="large"
                    class="custom-table"
                >
                    <div slot="avatarSlot" slot-scope="data, item" class="p-1">
                        <a-avatar
                            :size="30"
                            shape="square"
                            style="background-color: #3899ff; border-radius: 7px !important"
                            :username="item.name"
                            :src="$_.get(item, 'profilePicThumbObj.eurl')"
                        >
                            {{ item.name.substr(0, 1).toLocaleUpperCase() }}
                        </a-avatar>
                    </div>
                </a-table>
            </div>
            <div v-if="target === 'customer-groups'">
                <CustomerGroupsPicker :on-select-changed="onGroupSelected" :default-selected-group-id="clone.groupId" />
            </div>
        </div>
    </div>
</template>

<script>
import { tableSorter } from "../../../../../../utils/utils";
import { mapGetters, mapState } from "vuex";
import CustomerGroupsPicker from "../../../components/CustomerGroupsPicker";
import log2 from "../../../../../../utils/log2";
import _ from "lodash";
import { sendSocketMessage } from "../../../../../../utils/socketStream";

const log = log2("RecipientSelect");
export default {
    name: "RecipientSelect",
    components: { CustomerGroupsPicker },
    props: {
        value: {
            type: Object,
            required: true,
        },
        contactList: {
            type: Array,
            required: true,
        },
        channel: {
            type: Object,
            required: false,
        },
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({
            whatsappPlusEnabled: (state) => _.get(state.setting.settings, "whatsapp.plus.enabled", false),
            whatsappPlanExists: (state) =>
                _.get(state.setting.settings, "whatsapp.plus.enabled", false) ||
                _.get(state.setting.settings, "whatsapp.enabled", false),
            totalContacts: (state) => state.whatsapp.total,
            site: (state) => state.site.site,
        }),
    },
    mounted() {
        this.pagination.total = this.totalContacts;
    },
    data() {
        return {
            renderCount: 0,
            target: this.value.all ? "all" : "customer-groups",
            cols: [
                {
                    key: "profilePicThumbObj",
                    dataIndex: "profilePicThumbObj.eurl",
                    title: this.$t("message.profile"),
                    ...tableSorter("profilePicThumbObj", "string", this.currentSort),
                    scopedSlots: { customRender: "avatarSlot" },
                },
                {
                    key: "name",
                    dataIndex: "name",
                    title: this.$t("message.name"),
                    ...tableSorter("name", "string", this.currentSort),
                    scopedSlots: { customRender: "name" },
                },
                {
                    key: "id",
                    dataIndex: `id`,
                    title: this.$t("message.number"),
                    ...tableSorter("id", "string", this.currentSort),
                    scopedSlots: { customRender: "id" },
                },
            ],
            pagination: {
                total: 0,
                current: 1,
                defaultPageSize: 15,
                hideOnSinglePage: true,
                pageSizeOptions: ["10", "15", "20", "50", "100"],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            currentSort: {
                columnKey: "connectionTime",
                order: "ascend",
            },
            clone: _.cloneDeep(this.value),
        };
    },
    methods: {
        openNotificationWithIcon(featureType, type = "warning") {
            const messages = {
                customerGroup: {
                    message: this.$t("wp.wpPlusRequired"),
                    description:
                        "Customer Groups feature is only available for Whatsapp Plus Plan. Please upgrade your plan to use this feature.",
                },
            };
            const message = messages[featureType];
            const key = `plan`;
            this.$notification[type]({
                message: message.message,
                description: message.description,
                placement: "topRight",
                key,
                btn: (h) => {
                    return h(
                        "a-button",
                        {
                            props: {
                                type: "primary",
                                size: "small",
                            },
                            on: {
                                click: () => {
                                    log("clicked", this.$notification);
                                    this.$router.push("/settings/billing");
                                    this.$notification.close(key);
                                },
                            },
                        },
                        "Change Plan"
                    );
                },
            });
        },
        onChange(e) {
            log("onChange", e.target.value);
            const isChannelBroadcast = !!this.channel;
            if (e.target.value === "customer-groups" && !this.whatsappPlusEnabled && !isChannelBroadcast) {
                this.openNotificationWithIcon("customerGroup");
                this.target = "all";
                return;
            }
            this.target = e.target.value;
        },
        sendGetContactMessage() {
            sendSocketMessage("whatsapp get-contacts", {
                siteId: this.site._id,
                page: this.pagination.current,
                perPage: this.pagination.pageSize || this.pagination.defaultPageSize || 10,
            });
        },
        handleTableChange(pagination, filters, sorter) {
            this.pagination.current = pagination.current;
            this.pagination.pageSize = pagination.pageSize;
            this.sendGetContactMessage();
            this.renderCount++;
        },
        onGroupSelected(customerGroup) {
            log("onGroupSelected", customerGroup);
            this.clone = {
                ...this.clone,
                all: false,
                groupId: customerGroup._id,
                customNumbers: customerGroup.contacts,
            };
        },
    },
    watch: {
        watch: {
            totalContacts(nV, oV) {
                this.pagination.total = this.totalContacts;
            },
        },
        clone() {
            this.$emit("input", this.clone);
        },
        target(newTarget) {
            if (newTarget === "all") {
                this.clone = {
                    ...this.clone,
                    all: true,
                    groupId: null,
                    customNumbers: [],
                };
            }
        },
    },
};
</script>

<style scoped></style>
